import React from 'react';
import { format, isValid } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Calendar as CalendarIcon } from 'lucide-react';

interface IDateBoxProps {
	name: string;
	value?: string | number | Date;
	onValueChanged: (value?: any) => void;
	label?: string;
	error?: string;
	sizeClass?: 'xs' | 'sm' | 'md' | 'lg';
	helpBlock?: string;
	placeholder?: string;
	labelSmall?: boolean;
	isDisabled?: boolean;
	hasValid?: boolean;
	minDate?: Date;
	maxDate?: Date;
	className?: string;
	typeDate?: 'datetime' | 'date' | 'time';
	displayFormat?: string;
	isViewMode?: 'dot_bottom' | 'line_bottom';
}

const defaultDate = '1900-01-01';

export const DateBox = (props: IDateBoxProps) => {
	const {
		value,
		error,
		name,
		label,
		isDisabled,
		helpBlock,
		labelSmall,
		hasValid,
		placeholder,
		minDate,
		maxDate,
		onValueChanged,
		className,
		displayFormat = 'dd/MM/yyyy',
		isViewMode,
	} = props;
	const showError = !!error;

    // Parse incoming value to a safe Date object
    const dateValue = React.useMemo(() => {
        if (!value) return undefined;
        const d = new Date(value);
        return isValid(d) && d.getFullYear() > 1900 ? d : undefined;
    }, [value]);

	return (
		<div className="space-y-2">
			{label && (
				<Label htmlFor={name} className={cn(labelSmall && "text-xs")}>
					{label} {hasValid && <span className='text-destructive'>(*)</span>}
				</Label>
			)}
			<div className={cn("relative", className)}>
                <Popover>
                    <PopoverTrigger asChild>
                    <Button
                        variant={"outline"}
                        disabled={isDisabled || !!isViewMode}
                        className={cn(
                        "w-full justify-start text-left font-normal",
                        !dateValue && "text-muted-foreground",
                        showError && "border-destructive"
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateValue ? format(dateValue, displayFormat, { locale: vi }) : <span>{placeholder || 'Chọn ngày'}</span>}
                    </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={dateValue}
                        onSelect={(date) => {
                            if (date) {
                                // For simplicity, we just send ISO string. Adjust if necessary.
                                onValueChanged(date.toISOString());
                            } else {
                                onValueChanged(defaultDate);
                            }
                        }}
                        disabled={(date) => {
                            if (minDate && date < minDate) return true;
                            if (maxDate && date > maxDate) return true;
                            return false;
                        }}
                        initialFocus
                        locale={vi as any}
                    />
                    </PopoverContent>
                </Popover>
			</div>
			{showError && <p className="text-sm font-medium text-destructive">{error}</p>}
			{helpBlock && <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: helpBlock }} />}
		</div>
	);
};
